/**
 * Les noms de ce que chaque main joue, tels que le rail les écrit.
 *
 * Pur : ni React, ni DOM, ni transport. Le dessin est dans `score-layout.ts` ;
 * ce qui est ici est le choix des noms et de leur place, où il se teste. Même
 * partage que `rhythm.ts`, dont ce module lit les portes.
 *
 * ## Les noms sont sur le rail, et non à côté des notes
 *
 * La basse a d'abord vécu dans la ligne d'accord, où une classe que la main
 * droite ne jouait pas ouvrait une colonne. C'était sans conséquence tant que
 * la gauche tenait la fondamentale ; à la basse publiée elle joue la ligne
 * écrite, qui traverse des notes de passage, et la ligne d'accord annonçait
 * alors comme note de l'accord une note qui n'en est pas une. Voir
 * `chord-notes.ts`. Les deux rôles restent donc séparés : la ligne dit
 * l'accord, et rien d'autre.
 *
 * Elle a ensuite vécu à gauche des notes de l'accord, au milieu de l'écran,
 * avec la file de ce qui venait. Le nom d'une touche répond à « quoi jouer », et
 * cette question-là se pose **au moment de la frappe** : c'est le rail, et lui
 * seul, qui situe cet instant. Séparer les deux faisait faire à l'œil un
 * aller-retour pendant qu'on joue, c'est-à-dire au moment où il n'en a pas le
 * temps. Les noms sont donc posés contre leur portée, à l'aplomb des frappes
 * qu'ils nomment.
 *
 * ## Une seule règle pour les deux mains
 *
 * `laneLabels` étiquette une voie : un nom par **changement** de nom, et non un
 * par frappe. Les deux mains l'appellent, et ce qu'elles nomment diffère —
 * `bassLabels` nomme la touche la plus grave de la gauche, `chordLabels` nomme
 * l'accord que la droite tient — mais la règle de placement, elle, est écrite
 * une fois.
 *
 * Elle donne des résultats très différents d'une voie à l'autre, et c'est
 * normal : la gauche change de note à l'intérieur d'une fenêtre, la droite
 * rejoue le même accord à chaque impulsion. Deux noms par fenêtre en médiane à
 * gauche, quatre au neuvième décile ; **un seul** à droite, sur sa première
 * frappe. Ce n'est pas un cas particulier de la droite, c'est la même règle
 * appliquée à un fait différent.
 *
 * ## Une étiquette par changement de nom, et non une par frappe
 *
 * Toutes les frappes restent dessinées par le rail ; seul le nom n'est écrit
 * qu'une fois, à la première frappe qui le porte, et il vaut jusqu'au suivant.
 * Une fenêtre médiane porte trois frappes de la gauche pour deux noms
 * distincts, neuf frappes pour quatre noms au neuvième décile : répéter le nom
 * sous chaque frappe ferait une trame de lettres là où il faut lire des masses.
 *
 * Le nom qui revient se réécrit : `Do Fa Do` fait trois étiquettes. Ce qui est
 * écrit vaut jusqu'à l'étiquette suivante, donc taire le second `Do` ferait
 * lire `Fa` sur une touche qui n'en est pas une.
 *
 * ## Le nom de basse se replie à l'octave, et la portée dit le reste
 *
 * Deux `Do` à deux octaves portent le même nom et ne font qu'une étiquette. Ce
 * n'est pas une perte : la portée les écrit à sept degrés l'un de l'autre, et
 * l'octave se lit là. C'est aussi ce qui a fait disparaître la flèche de
 * direction qui accompagnait l'annonce de l'accord suivant : elle disait d'un
 * signe ce que la portée montre d'une ligne, et l'annonce elle-même a cédé la
 * place à ce qui est gravé plus à droite sur le ruban.
 *
 * ## Une porte peut n'appartenir qu'à une main
 *
 * L'autre tient alors ses touches. Elle ne frappe pas, donc elle ne change pas
 * de note, donc il n'y a rien à écrire : la porte est sautée sans que le nom
 * courant s'interrompe.
 *
 * ## Une frappe à plusieurs touches est sa note la plus grave
 *
 * La gauche peut plaquer deux notes ; le **nom** est alors celui de la plus
 * grave, comme partout ailleurs dans ce code. La portée, elle, les grave toutes
 * les deux : nommer n'est pas graver, et une main qui plaque une octave n'a
 * qu'un nom à lire pour deux notes à poser.
 */

import type { RhythmGate } from "@/lib/harmony/rhythm";
import { windowToneName } from "@/lib/music/theory-helpers";

/** Une fenêtre d'accord, réduite à ce que les noms en lisent. */
export type HandWindow = {
  readonly gates: readonly RhythmGate[];
  /** L'orthographe de la fondamentale, qui décide des bémols et des dièses. */
  readonly rootSpelling: string;
  readonly rootPitchClass: number;
  /** Les classes de hauteur que l'accord énonce. Voir `windowToneName`. */
  readonly chordPitchClasses?: ReadonlySet<number> | null;
  /** L'orthographe de la tonique du morceau. Voir `windowToneName`. */
  readonly keyTonic?: string | null;
  /** Ce que l'analyseur a écrit, touche par touche. Voir `windowToneName`. */
  readonly spelling?: ReadonlyMap<number, string> | null;
};

/** Un nom écrit contre une portée, et la frappe où il commence. */
export type RailLabel = {
  /**
   * Le rang de la porte qui ouvre ce nom, dans les portes de la fenêtre.
   *
   * Le rang et non une fraction : c'est le rail qui connaît la géométrie, et
   * c'est lui qui sait où la porte tombe. Voir `RhythmGate.at`.
   */
  readonly at: number;
  /**
   * Le nom, tel qu'il s'écrit sur la voie.
   *
   * Une **note** sur la voie de la gauche, sans octave, dans l'orthographe de
   * l'accord qui la porte : la notation du joueur s'y applique au dessin, comme
   * pour les notes de l'accord. Un **nom d'accord** sur celle de la droite,
   * déjà écrit par l'appelant, puisqu'il n'a pas d'orthographe à choisir.
   */
  readonly name: string;
};

/**
 * Les noms que la main gauche joue dans la fenêtre, un par changement.
 *
 * Propriété de la fenêtre et de rien d'autre. Ils ne bougent pas quand le
 * joueur avance : seule la tête de lecture bouge, et le rail la dessine à
 * part.
 */
export function bassLabels(window: HandWindow): readonly RailLabel[] {
  return laneLabels(window.gates.map((_, at) => bassOf(window, at)?.name ?? null));
}

/**
 * Le nom de l'accord, sur les frappes de la main droite.
 *
 * Le nom vient de l'appelant, tout écrit : c'est celui que l'écran affiche déjà
 * en grand au-dessus des notes, et le rail ne peut pas en dire un autre. Le
 * calculer ici demanderait de connaître la notation du joueur, que ce module
 * n'a pas à connaître, et surtout permettrait aux deux de diverger.
 *
 * Une fenêtre est un accord : la droite ne change donc pas de nom en la
 * traversant, et la règle rend une seule étiquette, sur sa première frappe.
 * C'est bien la même règle qui s'applique, appliquée à une main qui répète.
 */
export function chordLabels(window: HandWindow, name: string): readonly RailLabel[] {
  return laneLabels(window.gates.map((gate) => (gate.right.length === 0 ? null : name)));
}

/**
 * La règle des deux voies : un nom par changement de nom.
 *
 * Elle ne lit qu'une chose, ce que la main frappe porte comme nom à chaque
 * porte, et `null` quand elle ne frappe pas. Tout ce qui distingue une main de
 * l'autre est déjà résolu à ce moment-là, ce qui est exactement ce qui permet
 * d'écrire la règle une fois.
 */
function laneLabels(struck: readonly (string | null)[]): readonly RailLabel[] {
  const out: RailLabel[] = [];

  let written: string | null = null;

  for (let at = 0; at < struck.length; at += 1) {
    const name = struck[at] ?? null;

    if (name === null || name === written) {
      continue;
    }

    written = name;

    out.push({ at, name });
  }

  return out;
}

/**
 * Le cycle que la suite d'étiquettes répète, ou `null` quand elle n'en répète
 * aucun.
 *
 * `period` est la longueur du tour, `turns` le nombre de tours entamés, le
 * dernier pouvant être incomplet : une figure de quatre notes jouée trois fois
 * et demie rend `{ period: 4, turns: 4 }`.
 */
export type LabelLoop = {
  readonly period: number;
  readonly turns: number;
};

/**
 * Le cycle d'une suite d'étiquettes, quand elle en est un.
 *
 * ## Pourquoi le rail en a besoin
 *
 * Une main gauche qui arpège écrit sa figure autant de fois qu'elle la joue :
 * `Si Ré Fa♯ Ré` répété trois fois et demie fait **quatorze** mots pour trois
 * notes. Ça se lit, et c'est le problème : la ligne devient une partition en
 * lettres là où elle devait nommer ce qu'on joue. Une figure répétée est une
 * chose, pas quatorze, et il suffit de l'écrire une fois puis de dire qu'elle
 * revient.
 *
 * Mesuré sur 3 501 fenêtres du catalogue : 12 % des suites bouclent, mais
 * **34 % de celles qui portent quatre étiquettes ou plus**, c'est-à-dire
 * exactement celles où la ligne s'encombre. Longueurs de tour observées : 2
 * (240 fois), 3 (156), 4 (13), au-delà anecdotique.
 *
 * ## Strict, et c'est le point
 *
 * `Si Ré Fa♯ Ré Si Ré Fa♯ Sol` ne boucle pas, à un mot près, et rend donc
 * `null` : tout s'écrit, comme avant. Marquer une reprise qui n'en est pas une
 * ferait jouer une note pour une autre, quand ne rien marquer ne coûte que de
 * la place.
 *
 * Deux tours entiers au minimum. Un tour et demi n'est pas une répétition, et
 * l'écrire comme telle demanderait au joueur de deviner où le motif s'arrête.
 *
 * La période commence à deux : deux étiquettes voisines ne portent jamais le
 * même nom, `laneLabels` ayant déjà fondu les répétitions immédiates.
 */
export function labelLoop(labels: readonly RailLabel[]): LabelLoop | null {
  const n = labels.length;

  for (let period = 2; period * 2 <= n; period += 1) {
    let boucle = true;

    for (let at = period; at < n && boucle; at += 1) {
      boucle = labels[at].name === labels[at % period].name;
    }

    if (boucle) {
      return { period, turns: Math.ceil(n / period) };
    }
  }

  return null;
}

/** Une frappe de la main gauche, telle que ce module la lit. */
type Struck = {
  readonly midi: number;
  readonly name: string;
};

/**
 * La basse d'une porte, ou `null` quand la gauche n'y frappe pas.
 *
 * `gate.left` est croissant, par construction de `keysAt` : la plus grave est
 * donc la première.
 */
function bassOf(window: HandWindow, at: number): Struck | null {
  const midi = window.gates[at]?.left[0];

  if (midi === undefined) {
    return null;
  }

  return {
    midi,
    name: windowToneName({
      spelled: window.spelling?.get(midi) ?? null,
      pitchClass: classOf(midi),
      rootSpelling: window.rootSpelling,
      rootPitchClass: window.rootPitchClass,
      chordPitchClasses: window.chordPitchClasses ?? null,
      keyTonic: window.keyTonic ?? null,
    }),
  };
}

function classOf(midi: number): number {
  return ((midi % 12) + 12) % 12;
}
